/**
 * Upgrade-card icon helper. Every upgrade renders a clean generated icon
 * (matte-cartoon style, transparent background) — generator-tier upgrades key
 * off the generator id, everything else off the upgrade id. Falls back to a
 * neutral spark SVG if an image is missing (no emoji).
 */
import type { UpgradeConfig } from '../game/config/upgrades.js';

const FALLBACK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l2.2 5.8L20 11l-5.8 2.2L12 19l-2.2-5.8L4 11l5.8-2.2z"/></svg>';

/** Resolve the image src for an upgrade, or '' when it should use the fallback. */
function iconSrc(up: UpgradeConfig): string {
  if (up.category === 'generator' && up.effect.generatorId) {
    return `assets/upgrades/${up.effect.generatorId}.png`;
  }
  return `assets/upgrades/${up.id}.png`;
}

const TIER_NUM = ['I', 'II', 'III'];

/** Generator upgrades come in 3 tiers sharing one device icon; a tier ring +
 *  numeral badge distinguishes tier 1/2/3 so they never look identical. */
function tierOf(up: UpgradeConfig): number {
  if (up.category !== 'generator') return 0;
  const m = up.id.match(/_t(\d)$/);
  return m ? parseInt(m[1], 10) : 0;
}

export function upgradeIconHtml(up: UpgradeConfig): string {
  const src = iconSrc(up);
  const inner = src
    ? `<img class="upg-icon-img" src="${src}" alt="" onerror="this.outerHTML='<span class=&quot;upg-icon-fallback&quot;>${FALLBACK.replace(/"/g, '&quot;')}</span>'" />`
    : `<span class="upg-icon-fallback">${FALLBACK}</span>`;
  const tier = tierOf(up);
  if (!tier) return inner;
  return `<span class="upg-tier upg-tier-${tier}">${inner}<span class="upg-tier-badge">${TIER_NUM[tier - 1]}</span></span>`;
}
